import { Link } from 'react-router-dom';
import { CPUIcon, ChatIcon } from '@/icons';

interface DashboardStatsProps {
  agentCount: number;
  conversationCount: number;
  loading: boolean;
}

export function DashboardStats({ agentCount, conversationCount, loading }: DashboardStatsProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-8">
      {/* Conversations Card - Entire card is tappable on mobile */}
      <Link
        to="/conversations"
        className="block bg-neutral-100 dark:bg-neutral-800 rounded-lg border border-neutral-300 dark:border-neutral-700 p-4 sm:p-6 hover:border-blue-500 dark:hover:border-blue-500 transition-colors group"
        data-testid="conversations-card"
      >
        <h2 className="text-lg sm:text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-3 sm:mb-4">
          Conversations
        </h2>
        <div className="bg-neutral-200 dark:bg-neutral-900 rounded px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300 mb-4 min-h-[48px] flex items-center">
          {loading ? (
            <span className="text-neutral-500">Loading...</span>
          ) : conversationCount === 0 ? (
            <div className="flex items-center gap-3">
              <ChatIcon className="text-neutral-400 flex-shrink-0" />
              <div>
                <p className="text-neutral-600 dark:text-neutral-400 font-medium">
                  No conversations yet
                </p>
                <p className="text-xs text-neutral-500">
                  Start chatting by creating a new conversation
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 font-mono">
              <ChatIcon className="text-green-500" />
              <span>
                {conversationCount} {conversationCount === 1 ? 'conversation' : 'conversations'}
              </span>
            </div>
          )}
        </div>
        <span
          data-testid="view-conversations-button"
          className="block w-full px-4 py-3 sm:py-2 bg-blue-600 group-hover:bg-blue-700 text-white rounded-lg font-medium transition-colors text-center min-h-[48px] flex items-center justify-center"
        >
          {conversationCount === 0 && !loading ? 'New Conversation' : 'View Conversations'}
        </span>
      </Link>

      {/* Agents Card - Entire card is tappable on mobile */}
      <Link
        to="/agents"
        className="block bg-neutral-100 dark:bg-neutral-800 rounded-lg border border-neutral-300 dark:border-neutral-700 p-4 sm:p-6 hover:border-blue-500 dark:hover:border-blue-500 transition-colors group"
        data-testid="agents-card"
      >
        <h2 className="text-lg sm:text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-3 sm:mb-4">
          Your Agents
        </h2>
        <div className="bg-neutral-200 dark:bg-neutral-900 rounded px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300 mb-4 min-h-[48px] flex items-center">
          {loading ? (
            <span className="text-neutral-500">Loading...</span>
          ) : agentCount === 0 ? (
            <div className="flex items-center gap-3">
              <CPUIcon className="text-neutral-400 flex-shrink-0" />
              <div>
                <p className="text-neutral-600 dark:text-neutral-400 font-medium">No agents yet</p>
                <p className="text-xs text-neutral-500">
                  Create an AI agent to join your conversations
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 font-mono">
              <CPUIcon className="text-blue-500" />
              <span>
                {agentCount} {agentCount === 1 ? 'agent' : 'agents'}
              </span>
            </div>
          )}
        </div>
        <span
          data-testid="manage-agents-button"
          className="block w-full px-4 py-3 sm:py-2 bg-blue-600 group-hover:bg-blue-700 text-white rounded-lg font-medium transition-colors text-center min-h-[48px] flex items-center justify-center"
        >
          {agentCount === 0 && !loading ? 'Create First Agent' : 'Manage Agents'}
        </span>
      </Link>
    </div>
  );
}
